// RecoveryPage.jsx
// Page de récupération des données perdues

import React, { useState } from 'react';
import { supabase } from './supabaseClient';

function RecoveryPage() {
  const [recoveryStatus, setRecoveryStatus] = useState('idle');
  const [foundMatches, setFoundMatches] = useState([]);
  const [localStorageData, setLocalStorageData] = useState(null);
  
  // 1. RÉCUPÉRATION DEPUIS SUPABASE
  const recoverFromSupabase = async () => {
    setRecoveryStatus('searching_supabase');
    
    try {
      // Récupérer TOUS les matchs
      const { data: matches, error } = await supabase
        .from('matches')
        .select(`
          *,
          engineState
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      console.log('✅ Matchs trouvés dans Supabase:', matches?.length || 0);
      setFoundMatches(matches || []);
      setRecoveryStatus('found_supabase');
      
      return matches;
    } catch (error) {
      console.error('❌ Erreur Supabase:', error);
      setRecoveryStatus('error_supabase');
      return null;
    }
  };
  
  // 2. RÉCUPÉRATION DEPUIS LOCALSTORAGE
  const recoverFromLocalStorage = () => {
    setRecoveryStatus('searching_localstorage');
    
    try {
      // Lister TOUTES les clés du localStorage
      const allKeys = Object.keys(localStorage);
      console.log('🔍 Clés localStorage trouvées:', allKeys);
      
      // Chercher les clés liées aux matchs
      const matchKeys = allKeys.filter(key => 
        key.includes('match') || 
        key.includes('Match') ||
        key.includes('tennis') ||
        key.includes('score') ||
        key.includes('engine')
      );
      
      console.log('🎾 Clés de matchs trouvées:', matchKeys);
      
      // Récupérer les données
      const foundData = {};
      matchKeys.forEach(key => {
        try {
          const value = localStorage.getItem(key);
          foundData[key] = JSON.parse(value);
        } catch {
          foundData[key] = localStorage.getItem(key);
        }
      });
      
      console.log('📦 Données trouvées:', foundData);
      setLocalStorageData(foundData);
      setRecoveryStatus('found_localstorage');
      
      return foundData;
    } catch (error) {
      console.error('❌ Erreur localStorage:', error);
      setRecoveryStatus('error_localstorage');
      return null;
    }
  };
  
  // 3. EXPORT DES DONNÉES RÉCUPÉRÉES
  const exportRecoveredData = (data, source) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recovered_matches_${source}_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('💾 Export réussi:', a.download);
  };
  
  // 4. VIDER COMPLÈTEMENT LE LOCALSTORAGE (DEBUG)
  const dumpAllLocalStorage = () => {
    const allData = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      try {
        allData[key] = JSON.parse(localStorage.getItem(key));
      } catch {
        allData[key] = localStorage.getItem(key);
      }
    }
    
    console.log('📦 DUMP COMPLET localStorage:', allData);
    exportRecoveredData(allData, 'all_localstorage');
  };
  
  return (
    <div style={{
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ marginTop: 0 }}>🔧 Récupération de données</h1>
        
        <div style={{
          backgroundColor: '#fff3e0',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
          borderLeft: '4px solid #ff9800'
        }}>
          <strong>⚠️ Important :</strong> Cette page permet de récupérer les matchs perdus après une mise à jour de l'application.
        </div>
        
        {/* MÉTHODE 1 : SUPABASE */}
        <div style={{
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px'
        }}>
          <h2 style={{ marginTop: 0 }}>1️⃣ Récupération depuis Supabase (Cloud)</h2>
          <p>Si vos matchs étaient synchronisés, ils sont sauvegardés dans le cloud.</p>
          
          <button
            onClick={recoverFromSupabase}
            disabled={recoveryStatus === 'searching_supabase'}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              backgroundColor: '#1976D2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              marginRight: '12px'
            }}
          >
            {recoveryStatus === 'searching_supabase' ? '⏳ Recherche...' : '🔍 Chercher dans le cloud'}
          </button>
          
          {foundMatches.length > 0 && (
            <>
              <div style={{ 
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#c8e6c9',
                borderRadius: '8px',
                color: '#2e7d32'
              }}>
                ✅ <strong>{foundMatches.length} match(s) trouvé(s) !</strong>
              </div>
              
              <button
                onClick={() => exportRecoveredData(foundMatches, 'supabase')}
                style={{
                  marginTop: '12px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                💾 Exporter les matchs ({foundMatches.length})
              </button>
              
              <div style={{ marginTop: '16px' }}>
                <h4>Matchs trouvés :</h4>
                <ul>
                  {foundMatches.slice(0, 10).map((match, i) => (
                    <li key={i}>
                      {match.prenomJoueur1 || 'Joueur 1'} vs {match.prenomJoueur2 || 'Joueur 2'} 
                      {' - '}
                      {new Date(match.created_at).toLocaleDateString('fr-FR')}
                    </li>
                  ))}
                  {foundMatches.length > 10 && <li>... et {foundMatches.length - 10} autres</li>}
                </ul>
              </div>
            </>
          )}
        </div>
        
        {/* MÉTHODE 2 : LOCALSTORAGE */}
        <div style={{
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: '#f3e5f5',
          borderRadius: '8px'
        }}>
          <h2 style={{ marginTop: 0 }}>2️⃣ Récupération depuis le navigateur (Local)</h2>
          <p>Chercher les données stockées localement dans votre navigateur.</p>
          
          <button
            onClick={recoverFromLocalStorage}
            disabled={recoveryStatus === 'searching_localstorage'}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              backgroundColor: '#7b1fa2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              marginRight: '12px'
            }}
          >
            {recoveryStatus === 'searching_localstorage' ? '⏳ Recherche...' : '🔍 Chercher localement'}
          </button>
          
          {localStorageData && Object.keys(localStorageData).length > 0 && (
            <>
              <div style={{ 
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#c8e6c9',
                borderRadius: '8px',
                color: '#2e7d32'
              }}>
                ✅ <strong>{Object.keys(localStorageData).length} entrée(s) trouvée(s) !</strong>
              </div>
              
              <button
                onClick={() => exportRecoveredData(localStorageData, 'localstorage')}
                style={{
                  marginTop: '12px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                💾 Exporter les données locales
              </button>
              
              <div style={{ marginTop: '16px' }}>
                <h4>Clés trouvées :</h4>
                <ul>
                  {Object.keys(localStorageData).map((key, i) => (
                    <li key={i}><code>{key}</code></li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
        
        {/* MÉTHODE 3 : DUMP COMPLET */}
        <div style={{
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: '#ffebee',
          borderRadius: '8px'
        }}>
          <h2 style={{ marginTop: 0 }}>3️⃣ Export complet (Mode debug)</h2>
          <p>Exporter TOUTES les données du navigateur (pour analyse détaillée).</p>
          
          <button
            onClick={dumpAllLocalStorage}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              backgroundColor: '#d32f2f',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            🗂️ Export complet localStorage
          </button>
        </div>
        
        {/* STATUT */}
        {recoveryStatus !== 'idle' && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#e0e0e0',
            borderRadius: '8px',
            fontSize: '14px'
          }}>
            <strong>Statut :</strong> {recoveryStatus}
          </div>
        )}
      </div>
    </div>
  );
}

export default RecoveryPage;